import { DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { startWith } from 'rxjs';
import { AuthService } from '../../auth';
import { CommonFormValidationMessagesComponent } from '../../forms';
import {
  LoaderGlobeAnimationDetailComponent,
  LoadingObject,
  LoadingService,
} from '../../loaders';
import { UserLinksService } from '../../profile-user/user-links.service';
import { ProfessionalCompleteness } from '../../profile-user/user-links.types';
import {
  CompletenessStatusCardRequirementItem,
  TrackingCompletenessStatusCardComponent,
} from '../../tracking/completeness-status-card';
import {
  InterculturalistProfileViewModel,
  InterculturalistProfileError,
  WorkStatus,
} from '../interculturalist-profile.types';
import { InterculturalistProfileService } from '../interculturalist-profile.service';

type ReviewTimelineState = 'draft' | 'requested' | 'in_review' | 'completed';
type ReviewOutcomeState = 'approved' | 'pre_approved' | 'not_approved' | 'skipped' | null; // Skipped means that the user was assigned the interculturalist role simply by an Administrator, not by going through the review process and not on registration via the 'RegistrationPending' method.

@Component({
  selector: 'multisite-professional-status',
  templateUrl: './professional-status.component.html',
  styleUrls: ['./professional-status.component.scss'],
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslocoModule,
    AlertModule,
    RouterLink,
    LoaderGlobeAnimationDetailComponent,
    CommonFormValidationMessagesComponent,
    TrackingCompletenessStatusCardComponent,
  ],
  providers: [provideTranslocoScope('profile')],
})
export class ProfessionalStatusComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly userLinksService = inject(UserLinksService);
  private readonly interculturalistProfileService = inject(InterculturalistProfileService);

  readonly workStatusOptions: Array<{ value: WorkStatus; translationKey: string }> = [
    { value: 'working', translationKey: 'profile.interculturalist_status_option_1' },
    { value: 'break', translationKey: 'profile.interculturalist_status_option_2' },
    { value: 'preparing', translationKey: 'profile.interculturalist_status_option_3' },
    { value: 'curious', translationKey: 'profile.interculturalist_status_option_4' },
    { value: 'other', translationKey: 'profile.interculturalist_status_option_5' },
  ];

  readonly user = toSignal(this.authService.user, {
    initialValue: this.authService.user.getValue(),
  });

  readonly interculturalistProfile = signal<InterculturalistProfileViewModel | null | undefined>(undefined);
  readonly professionalCompleteness = signal<ProfessionalCompleteness>({
    hasLinkedin: false,
    hasOrganisation: false,
    hasNetwork: false,
    hasPublication: false,
    isComplete: false,
  });
  readonly interculturalistRoleAssignedOnRegistration = signal<boolean | undefined>(undefined);
  readonly submitAttempted = signal(false);
  readonly error = signal<string | null>(null);
  readonly loadingObject = signal<LoadingObject>({});

  readonly statusForm = new FormGroup({
    self_assessment: new FormControl<boolean>(false, {
      nonNullable: true,
    }),
    work_status: new FormControl<WorkStatus | ''>('', {
      nonNullable: true,
    }),
    statement: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(750)],
    }),
  });

  private readonly formValue = toSignal(
    this.statusForm.valueChanges.pipe(startWith(this.statusForm.getRawValue())),
    { initialValue: this.statusForm.getRawValue() },
  );

  readonly initialLoading = computed(() => this.interculturalistProfile() === undefined);

  readonly isAnythingLoading = computed<boolean>(() => {
    const profileLoading = this.interculturalistProfileService.loadingObjectSignal()['interculturalistProfile'] === true;
    const reviewLoading = this.interculturalistProfileService.loadingObjectSignal()['toggleReviewRequest'] === true;
    const roleAssignedOnRegistrationLoading = this.interculturalistProfileService.loadingObjectSignal()['interculturalistRoleAssignedOnRegistration'] === true;
    const linksLoading = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    return profileLoading || reviewLoading || roleAssignedOnRegistrationLoading || linksLoading || this.loadingService.isAnythingLoading(this.loadingObject());
  });

  readonly formDisabled = computed(() => this.initialLoading() || this.isAnythingLoading());
  readonly isInterculturalist = computed(() => {
    const roles = this.user()?.roles ?? [];
    // return roles.some((role) => role.title === 'Interculturalist');
    return this.authService.checkRole('Interculturalist');
  });

  readonly requiresAdditionalDetails = computed(() => this.formValue().self_assessment === true);

  readonly hasUnsavedChanges = computed(() => {
    const storedComparable = this.getComparableFormValuesFromProfile(this.interculturalistProfile());
    const currentComparable = this.getComparableFormValuesFromRaw(this.formValue());

    return storedComparable.self_assessment !== currentComparable.self_assessment
      || storedComparable.work_status !== currentComparable.work_status
      || storedComparable.statement !== currentComparable.statement;
  });

  readonly linkRequirementMet = computed(() => this.professionalCompleteness().isComplete);

  readonly formRequirementsMet = computed(() => {
    const currentComparable = this.getComparableFormValuesFromRaw(this.formValue());

    return currentComparable.self_assessment === true
      && Boolean(currentComparable.work_status)
      && currentComparable.statement.length >= 80;
  });

  readonly statusCompletenessMet = computed(() => this.linkRequirementMet() && this.formRequirementsMet());

  readonly canRequestReview = computed(() => {
    const interculturalistProfile = this.interculturalistProfile();
    const reviewState = this.reviewTimelineState();
    const alreadyHasInterculturalistRole = this.isInterculturalist();
    const canRequestFromCurrentState = reviewState === 'draft' || (reviewState === 'completed' && !this.isInterculturalist());

    return Boolean(interculturalistProfile?.id)
      && canRequestFromCurrentState
      && !alreadyHasInterculturalistRole
      && this.statusCompletenessMet()
      && !this.hasUnsavedChanges()
      && !this.isAnythingLoading();
  });

  readonly canWithdrawReview = computed(() => {
    const reviewState = this.reviewTimelineState();
    return (reviewState === 'requested' || reviewState === 'in_review') && !this.isAnythingLoading();
  });

  readonly reviewTimelineState = computed<ReviewTimelineState>(() => {
    const interculturalistProfile = this.interculturalistProfile();
    if (interculturalistProfile?.reviewCompletedAt || this.isInterculturalist()) {
      return 'completed';
    }

    if (interculturalistProfile?.reviewStartedAt) {
      return 'in_review';
    }

    if (interculturalistProfile?.reviewRequestedAt) {
      return 'requested';
    }

    return 'draft';
  });

  interculturalistStatusTranslation = computed<{scope: string | undefined, key: string}>(() => {

    if (this.isInterculturalist()) {
      return { scope: undefined, key: 'common.active' };
    }

    const reviewState = this.reviewTimelineState();

    if (reviewState === 'requested' || reviewState === 'in_review') {
      return { scope: 'profile', key: 'profile.requested' };
    }

    return { scope: 'profile', key: 'profile.not_requested' };
  });

  readonly reviewOutcome = computed<ReviewOutcomeState>(() => {

    const interculturalistRoleAssignedOnRegistration = this.interculturalistRoleAssignedOnRegistration();

    if (interculturalistRoleAssignedOnRegistration === true) {
      return 'pre_approved';
    }

    const interculturalistProfile = this.interculturalistProfile();

    if(this.isInterculturalist() && (!interculturalistProfile || !interculturalistProfile.reviewCompletedAt)) {
      return 'skipped';
    }

    if (!interculturalistProfile?.reviewCompletedAt) {
      return null;
    }

    if (!this.isInterculturalist()) {
      return 'not_approved';
    }

    return 'approved';
  });

  readonly completenessRequirementItems = computed<CompletenessStatusCardRequirementItem[]>(() => {
    const currentComparable = this.getComparableFormValuesFromRaw(this.formValue());
    const professionalLinks = this.professionalCompleteness();
    const hasProfessionalLink = professionalLinks.hasOrganisation || professionalLinks.hasNetwork || professionalLinks.hasPublication;

    return [
      {
        id: 'self-assessment',
        met: currentComparable.self_assessment === true,
        translationKey: 'profile.status_requirement_self_assessment',
        translationScope: 'profile',
      },
      {
        id: 'work-status',
        met: Boolean(currentComparable.work_status),
        translationKey: 'profile.status_requirement_work_status',
        translationScope: 'profile',
      },
      {
        id: 'statement',
        met: currentComparable.statement.length >= 80,
        translationKey: 'profile.status_requirement_statement',
        translationScope: 'profile',
      },
      {
        id: 'linkedin',
        met: professionalLinks.hasLinkedin,
        translationKey: 'profile.interculturalist_status_requirement_linkedin',
        translationScope: 'profile',
      },
      {
        id: 'professional-link',
        met: hasProfessionalLink,
        translationKey: 'profile.interculturalist_status_requirement_professional_link',
        translationScope: 'profile',
      },
    ];
  });

  constructor() {
    effect(() => {
      const workStatusControl = this.statusForm.controls.work_status;
      const statementControl = this.statusForm.controls.statement;

      if (this.requiresAdditionalDetails()) {
        workStatusControl.setValidators([Validators.required]);
        statementControl.setValidators([Validators.required, Validators.minLength(80), Validators.maxLength(750)]);
      } else {
        workStatusControl.clearValidators();
        statementControl.setValidators([Validators.maxLength(750)]);
      }

      workStatusControl.updateValueAndValidity({ emitEvent: false });
      statementControl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      if (this.formDisabled()) {
        this.statusForm.disable({ emitEvent: false });
      } else {
        this.statusForm.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.interculturalistProfileService.interculturalistProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((interculturalistProfile) => {
        const previousProfile = this.interculturalistProfile();
        this.interculturalistProfile.set(interculturalistProfile);

        if (!this.statusForm.dirty || previousProfile === undefined || previousProfile === null) {
          this.patchFormFromProfile(interculturalistProfile);
        }
      });

    this.userLinksService.professionalCompleteness$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((completeness) => {
        this.professionalCompleteness.set(completeness);
      });

    this.interculturalistProfileService.interculturalistRoleAssignedOnRegistration$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.interculturalistRoleAssignedOnRegistration.set(value);
      });

    this.requestInitialProfileIfNeeded();
    this.requestInitialLinksIfNeeded();
    this.requestInitialInterculturalistRoleAssignedOnRegistrationIfNeeded();
  }

  saveStatus(): void {
    this.submitAttempted.set(true);

    if (this.isAnythingLoading()) {
      return;
    }

    if (this.statusForm.invalid) {
      this.statusForm.markAllAsTouched();
      return;
    }

    this.error.set(null);

    this.interculturalistProfileService
      .saveInterculturalistProfile(this.buildRequestBody())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (interculturalistProfile) => {
          this.patchFormFromProfile(interculturalistProfile);
          this.submitAttempted.set(false);
        },
        error: (errorResponse: InterculturalistProfileError | string) => {
          const matchedFieldErrors = this.applyFieldErrors(errorResponse);
          if (!matchedFieldErrors) {
            this.error.set(typeof errorResponse === 'string' ? errorResponse : errorResponse.message);
          }
        },
      });
  }

  toggleReviewRequest(): void {
    if (!this.canRequestReview() && !this.canWithdrawReview()) {
      return;
    }

    this.error.set(null);

    this.interculturalistProfileService
      .toggleReviewRequest()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.authService.getUser(true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            error: () => void 0,
          });
        },
        error: (errorResponse: InterculturalistProfileError | string) => {
          this.error.set(typeof errorResponse === 'string' ? errorResponse : errorResponse.message);
        },
      });
  }

  private requestInitialProfileIfNeeded(): void {
    const isLoading = this.interculturalistProfileService.loadingObjectSignal()['interculturalistProfile'] === true;
    const hasProfile = this.interculturalistProfileService.interculturalistProfileState !== undefined;

    if (!isLoading && !hasProfile) {
      this.interculturalistProfileService
        .getInterculturalistProfile()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorResponse: InterculturalistProfileError | string) => {
            this.error.set(typeof errorResponse === 'string' ? errorResponse : errorResponse.message);
          },
        });
    }
  }

  private requestInitialLinksIfNeeded(): void {
    const isLoading = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    const hasLinks = this.userLinksService.userLinksState.length > 0;

    if (!isLoading && !hasLinks) {
      this.userLinksService
        .fetchUserLinks()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorMessage) => {
            this.error.set(typeof errorMessage === 'string' ? errorMessage : 'error.could_not_load');
          },
        });
    }
  }

  private requestInitialInterculturalistRoleAssignedOnRegistrationIfNeeded(): void {
    const isLoading = this.interculturalistProfileService.loadingObjectSignal()['interculturalistRoleAssignedOnRegistration'] === true;
    const hasValue = this.interculturalistProfileService.interculturalistRoleAssignedOnRegistrationState !== undefined;

    if (!isLoading && !hasValue) {
      this.interculturalistProfileService
        .getInterculturalistRoleAssignedOnRegistration()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorResponse: InterculturalistProfileError | string) => {
            this.error.set(typeof errorResponse === 'string' ? errorResponse : errorResponse.message);
          },
        });
    }
  }

  private patchFormFromProfile(interculturalistProfile: InterculturalistProfileViewModel | null | undefined): void {
    const comparableValues = this.getComparableFormValuesFromProfile(interculturalistProfile);
    this.statusForm.reset({
      self_assessment: comparableValues.self_assessment,
      work_status: comparableValues.work_status ?? '',
      statement: comparableValues.statement,
    });
    this.statusForm.markAsPristine();
    this.statusForm.markAsUntouched();
  }

  private buildRequestBody() {
    const comparableValues = this.getComparableFormValuesFromRaw(this.formValue());

    return {
      self_assessment: comparableValues.self_assessment,
      work_status: comparableValues.work_status,
      statement: comparableValues.statement,
    };
  }

  private getComparableFormValuesFromProfile(interculturalistProfile: InterculturalistProfileViewModel | null | undefined) {
    return {
      self_assessment: interculturalistProfile?.selfAssessment === true,
      work_status: interculturalistProfile?.workStatus ?? null,
      statement: this.coerceToTrimmedString(interculturalistProfile?.statement),
    };
  }

  private getComparableFormValuesFromRaw(rawValue: { self_assessment?: boolean | null; work_status?: WorkStatus | '' | null; statement?: string | null; }) {
    return {
      self_assessment: rawValue.self_assessment === true,
      work_status: rawValue.work_status ? rawValue.work_status : null,
      statement: this.coerceToTrimmedString(rawValue.statement),
    };
  }

  private coerceToTrimmedString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private applyFieldErrors(errorResponse: InterculturalistProfileError | string): boolean {
    if (typeof errorResponse === 'string' || !errorResponse.errors) {
      return false;
    }

    const entries = Object.entries(errorResponse.errors);
    if (!entries.length) {
      return false;
    }

    entries.forEach(([fieldName, messages]) => {
      const control = this.statusForm.get(fieldName);
      if (!control) {
        return;
      }

      const messageList = Array.isArray(messages) ? messages : [messages];
      const backendMessage = String(messageList[0] ?? '').toLowerCase();
      const nextErrors: Record<string, boolean> = { ...(control.errors ?? {}) };

      if (backendMessage.includes('required')) {
        nextErrors.required = true;
      } else if (backendMessage.includes('at least') || backendMessage.includes('minimum')) {
        nextErrors.minlength = true;
      } else if (backendMessage.includes('greater than') || backendMessage.includes('maximum')) {
        nextErrors.maxlength = true;
      } else {
        nextErrors.backend = true;
      }

      control.setErrors(nextErrors);
      control.markAsTouched();
    });

    return true;
  }
}